<template>
  <div class="datasheet-print">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Datasheet") }}</h4>
        <b-badge
          v-if="!loading && students.length"
          variant="light-success"
          class="ml-2"
        >
          {{ students.length }} {{ $t("Student(s)") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" :disabled="loading" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-panel">
      <b-spinner variant="primary" class="mb-2" />
      <h5 class="mb-1">{{ $t("Loading Datasheet") }}</h5>
      <p class="text-muted mb-0">{{ loadingText }}</p>
    </div>

    <div v-else-if="error" class="state-panel">
      <feather-icon icon="AlertCircleIcon" size="36" class="text-danger mb-2" />
      <h5 class="text-danger mb-1">{{ $t("Unable to load datasheet") }}</h5>
      <p class="text-muted">{{ error }}</p>
      <button class="btn-modern btn-print mt-2" @click="loadData">
        <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
        {{ $t("Retry") }}
      </button>
    </div>

    <div v-else class="sheet-page">
      <!-- Same letterhead style as EarlyYearReport -->
      <header class="letterhead">
        <div class="letterhead__logo-wrap">
          <img
            v-if="schoolLogo"
            :src="schoolLogo"
            alt="School logo"
            class="letterhead__logo"
          />
          <div v-else class="letterhead__logo-fallback">
            <feather-icon icon="AwardIcon" size="36" />
          </div>
        </div>
        <h1 class="letterhead__school">{{ campusName }}</h1>
        <p class="letterhead__tagline">
          {{ $t("Pre-Junior to O-Level, Matric & Intermediate") }}
        </p>
        <p class="letterhead__note">
          {{ $t("(P-6061) Registered & Recognised by the Cambridge Board") }}
        </p>
        <p class="letterhead__exam">{{ examSessionTitle }}</p>
      </header>

      <div v-if="!students.length" class="empty-subjects">
        {{ $t("No student records found") }}
      </div>

      <div v-else class="students-flow">
        <section
          v-for="(student, studentIndex) in students"
          :key="'ds-' + (student.sID || studentIndex)"
          class="student-block"
        >
          <!-- Student / Father / CID on one line -->
          <table class="student-info-table">
            <tbody>
              <tr>
                <td
                  class="student-info-table__cell student-info-table__cell--name"
                >
                  <span class="student-info-table__label"
                    >{{ $t("Student Name") }}:</span
                  >
                  <span class="student-info-table__value">{{
                    student.name || "—"
                  }}</span>
                </td>
                <td
                  class="student-info-table__cell student-info-table__cell--father"
                >
                  <span class="student-info-table__label"
                    >{{ $t("Father Name") }}:</span
                  >
                  <span class="student-info-table__value">{{
                    student.fatherName || "—"
                  }}</span>
                </td>
                <td
                  class="student-info-table__cell student-info-table__cell--cid"
                >
                  <span class="student-info-table__label"
                    >{{ $t("CID") }}:</span
                  >
                  <span class="student-info-table__value">{{
                    student.grNo || student.sID || "—"
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="!student.categories.length"
            class="empty-subjects empty-subjects--sm"
          >
            {{ $t("No remarks found.") }}
          </div>

          <div
            v-else
            class="subjects-grid"
            :class="'subjects-grid--cols-' + colsPerRow"
          >
            <div
              v-for="(category, catIndex) in student.categories"
              :key="student.sID + '-' + category.key"
              class="subject-box"
              :class="{
                'subject-box--full': isLastOddInRow(
                  catIndex,
                  student.categories.length
                ),
              }"
            >
              <table class="subject-box__table">
                <colgroup>
                  <col />
                  <col
                    v-if="category.hasUrduRemarks"
                    class="subject-box__key-col"
                  />
                  <col
                    v-if="category.hasUrduRemarks"
                    class="subject-box__key-col"
                  />
                  <col v-else class="subject-box__key-col" />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      class="subject-box__title"
                      :colspan="category.hasUrduRemarks ? 3 : 2"
                    >
                      {{ category.title }}
                    </th>
                  </tr>
                  <tr
                    v-if="category.hasUrduRemarks"
                    class="subject-box__eu-head"
                  >
                    <th class="subject-box__eu-blank"></th>
                    <th class="subject-box__eu">E</th>
                    <th class="subject-box__eu">U</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in category.items"
                    :key="category.key + '-row-' + idx"
                  >
                    <td class="subject-box__criteria">
                      {{ item.optionName || "—" }}
                    </td>
                    <template v-if="category.hasUrduRemarks">
                      <td class="subject-box__key">
                        <span
                          v-if="formatKey(item.rating)"
                          class="key-pill"
                          :class="
                            'key-pill--' + formatKey(item.rating).toLowerCase()
                          "
                        >
                          {{ formatKey(item.rating) }}
                        </span>
                      </td>
                      <td class="subject-box__key">
                        <span
                          v-if="formatKey(item.urduRating)"
                          class="key-pill"
                          :class="
                            'key-pill--' +
                            formatKey(item.urduRating).toLowerCase()
                          "
                        >
                          {{ formatKey(item.urduRating) }}
                        </span>
                      </td>
                    </template>
                    <td v-else class="subject-box__key">
                      <span
                        v-if="formatKey(item.rating)"
                        class="key-pill"
                        :class="
                          'key-pill--' + formatKey(item.rating).toLowerCase()
                        "
                      >
                        {{ formatKey(item.rating) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BSpinner } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "DatasheetPrint",
  components: { BBadge, BSpinner },
  data() {
    return {
      loading: true,
      loadingText: "Loading datasheet…",
      error: null,
      campusName: "School",
      schoolLogo: null,
      meta: {
        className: "",
        sectionName: "",
        examTypeName: "",
        sessionName: "",
        date: "",
      },
      students: [],
    };
  },
  computed: {
    classSectionTitle() {
      return [this.meta.className, this.meta.sectionName]
        .filter(Boolean)
        .join(" - ");
    },
    examSessionTitle() {
      const exam = this.meta.examTypeName || this.$t("Assessment");
      const session = this.meta.sessionName
        ? ` Session ${this.meta.sessionName}`
        : "";
      const cls = this.classSectionTitle ? ` · ${this.classSectionTitle}` : "";
      return `${exam} ${this.$t("Datasheet")}${session}${cls}`;
    },
    normalizedClassName() {
      return String(this.meta.className || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");
    },
    isPreJuniorLayout() {
      const normalized = this.normalizedClassName;
      if (!normalized) return false;
      return (
        normalized.includes("PJR") ||
        normalized.includes("PREJNR") ||
        normalized.includes("PREJUNIOR") ||
        normalized.includes("PREJR")
      );
    },
    isJuniorLayout() {
      const normalized = this.normalizedClassName;
      if (!normalized) return false;
      if (this.isPreJuniorLayout) return false;

      let suffix = "";
      if (normalized.startsWith("JUNIOR")) {
        suffix = normalized.slice(6);
      } else if (normalized.startsWith("JR")) {
        suffix = normalized.slice(2);
      } else if (normalized.includes("JUNIOR")) {
        suffix = normalized.slice(normalized.indexOf("JUNIOR") + 6);
      } else {
        return false;
      }

      // Junior / Junior I / Junior II only
      if (/^(III|IV|VI{0,3}|IX|X|[3-9]|1[0-2])[A-Z]*$/.test(suffix)) {
        return false;
      }
      return /^(?:I{1,2}|1|2)?[A-Z]?$/.test(suffix);
    },
    // Junior 1/2 → 4 boxes; Pre-Junior → 6 boxes
    colsPerRow() {
      if (this.isPreJuniorLayout) return 6;
      if (this.isJuniorLayout) return 4;
      return 4;
    },
  },
  created() {
    this.initMetaFromQuery();
    this.loadCampusInfo();
    this.loadData();
  },
  methods: {
    initMetaFromQuery() {
      const q = this.$route.query;
      this.meta = {
        className: q.clsName || "",
        sectionName: q.secName || "",
        examTypeName: q.examTypeName || "",
        sessionName: q.sessionName || "",
        date: q.date || "",
      };
    },

    async loadCampusInfo() {
      const db = this.$route.query.db || this.$store.state.userData.db;
      const cID = this.$route.query.cID || this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;

      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db, cId: cID },
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (c) => String(c.id) === String(cID)
          );
          if (match) {
            this.campusName = match.name || this.campusName;
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => String(b.id) === String(cID)) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.name ||
            currentBranch.branch ||
            this.campusName;
          this.schoolLogo =
            currentBranch.logo || this.$store.state.logo || null;
        }
      }
    },

    async loadData() {
      const q = this.$route.query;
      const db = q.db || this.$store.state.userData.db;
      const cID = q.cID || this.$store.state.userData.cId;
      const classID = q.classID;
      const sectionID = q.sectionID;
      const examTypeID = String(q.examTypeID || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)[0];
      const sessionID = q.sessionID;

      if (!classID || !sectionID || !examTypeID || !sessionID) {
        this.error = this.$t(
          "Missing report filters. Please open this datasheet from Print Result."
        );
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;
      this.loadingText = this.$t("Loading datasheet…");

      try {
        const response = await axios.get(
          `${this.$store.state.domain}TabulationSheetMarks/GetStudentWiseByFilters`,
          {
            params: {
              db,
              cID,
              classID,
              sectionID,
              examTypeID,
              sessionID,
            },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        const payload = response.data;
        let rows = [];
        if (
          payload &&
          payload.status === "success" &&
          Array.isArray(payload.data)
        ) {
          rows = payload.data;
        } else if (Array.isArray(payload)) {
          rows = payload;
        } else if (payload && Array.isArray(payload.data)) {
          rows = payload.data;
        }

        const sIDs = String(q.sIDs || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);

        if (sIDs.length) {
          rows = rows.filter((row) => sIDs.includes(String(row.sID)));
        }

        this.students = rows.map((row) => ({
          sID: row.sID,
          name: row.name || row.studentName || "",
          fatherName: row.fatherName || row.father_name || "",
          grNo: row.grNo || row.gr_no || "",
          categories: this.buildCategories(row),
        }));
      } catch (e) {
        console.error("Datasheet load error:", e);
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          this.$t("Failed to load datasheet.");
      } finally {
        this.loading = false;
      }
    },

    buildCategories(student) {
      const allMarks = [];
      (student.records || []).forEach((record) => {
        (record.marksData || []).forEach((item) => {
          allMarks.push({
            ...item,
            // keep record-level subject name as fallback
            _recordSubject:
              record.subjectName || record.subject || record.name || "",
          });
        });
      });

      const categoryMap = {};
      allMarks.forEach((item) => {
        const title = String(
          item.categoryName ||
            item.subjectName ||
            item._recordSubject ||
            item.subject ||
            "OTHER"
        )
          .trim()
          .toUpperCase();

        if (!categoryMap[title]) {
          categoryMap[title] = {
            key: title,
            title,
            sortIndex: Number(item.categorySortIndex) || 999,
            items: [],
          };
        }

        categoryMap[title].items.push({
          optionName: item.optionName || item.questionName || "",
          rating: item.rating,
          urduRating: item.urduRating,
          urduRemarks: this.isUrduRemarksFlag(item),
          optionSortIndex: Number(item.optionSortIndex) || 0,
        });
      });

      return Object.values(categoryMap)
        .map((category) => {
          category.items.sort((a, b) => a.optionSortIndex - b.optionSortIndex);
          // E | U columns when dual-language remarks exist on any row
          category.hasUrduRemarks = category.items.some(
            (row) => row.urduRemarks
          );
          return category;
        })
        .sort((a, b) => a.sortIndex - b.sortIndex);
    },

    isUrduRemarksFlag(item) {
      const flag = item.urduRemarks;
      if (flag === true || flag === 1 || flag === "1") return true;
      if (String(flag || "").toLowerCase() === "true") return true;
      // Fallback: any non-empty Urdu rating means dual E/U columns
      return item.urduRating != null && String(item.urduRating).trim() !== "";
    },

    isLastOddInRow(index, total) {
      const cols = this.colsPerRow;
      const rem = total % cols;
      return rem === 1 && index === total - 1;
    },

    formatKey(value) {
      if (value === null || value === undefined || value === "") return "";
      return String(value).trim().toUpperCase();
    },

    print() {
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
$black: #000000;
$ink: #1a1a1a;
$line: #b0b0b0;
$line-strong: #9a9a9a;
$muted: #64748b;

.datasheet-print {
  min-height: 100vh;
  background: #e8e8e8;
  padding: 12px;
}

.no-print.control-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-modern {
  border: none;
  border-radius: 10px;
  padding: 0.5rem 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.btn-print {
  background: linear-gradient(135deg, #7c5dfa, #9d7aff);
  color: #fff;
}

.btn-close {
  background: #f1f5f9;
  color: #334155;
}

.state-panel {
  background: #fff;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
}

.sheet-page {
  background: #fff;
  border: 1px solid $line-strong;
  border-radius: 4px;
  padding: 10px 12px 14px;
}

/* ── Letterhead (match EarlyYearReport) ── */
.letterhead {
  text-align: center;
  margin-bottom: 8px;
}

.letterhead__logo-wrap {
  margin-bottom: 4px;
}

.letterhead__logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.letterhead__logo-fallback {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  border: 1px solid $line;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $black;
}

.letterhead__school {
  font-size: 24px;
  font-weight: 700;
  text-transform: none;
  text-decoration: none;
  letter-spacing: 0.01em;
  margin: 0 0 4px;
  color: $black;
  line-height: 1.25;
  font-family: Georgia, "Times New Roman", serif;
}

.letterhead__tagline,
.letterhead__note {
  font-size: 13px;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
}

.letterhead__exam {
  margin: 8px 0 0;
  font-size: 15px;
  font-weight: 700;
  color: $black;
  font-family: Georgia, "Times New Roman", serif;
}

/* ── Student info ── */
.student-info-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 6px;
  font-family: Georgia, "Times New Roman", serif;

  td {
    border: 1px solid $line-strong;
    vertical-align: middle;
    padding: 0;
  }
}

.student-info-table__cell {
  padding: 6px 8px !important;
  font-size: 12px;
  line-height: 1.35;
  color: $black;
  background: #fff;

  &--name {
    width: 42%;
  }

  &--father {
    width: 42%;
  }

  &--cid {
    width: 16%;
    white-space: nowrap;
  }
}

.student-info-table__label {
  font-weight: 400;
}

.student-info-table__value {
  font-weight: 700;
  margin-left: 4px;
}

.students-flow {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.student-block {
  page-break-inside: avoid;
  break-inside: avoid;
}

/* ── Subject boxes: Junior 4 / Pre-Junior 6 per row ── */
.subjects-grid {
  display: grid;
  gap: 0;
  border-top: 1px solid $line-strong;
  border-left: 1px solid $line-strong;

  &--cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  &--cols-6 {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

.subject-box {
  min-width: 0;
  border-right: 1px solid $line-strong;
  border-bottom: 1px solid $line-strong;
  background: #fff;

  &--full {
    grid-column: 1 / -1;
  }
}

.subject-box__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: none;
    border-bottom: 1px solid #e5e5e5;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.subject-box__key-col {
  width: 28px;
}

.subject-box__title {
  text-align: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 4px 4px !important;
  background: #f3f3f3 !important;
  color: $ink;
  border-bottom: 1px solid $line-strong !important;
}

.subject-box__eu-head th {
  background: #fafafa !important;
  border-bottom: 1px solid $line !important;
  padding: 2px 2px !important;
  font-size: 9px;
  font-weight: 800;
  text-align: center;
  color: $ink;
}

.subject-box__eu-blank {
  text-align: left !important;
}

.subject-box__eu {
  width: 28px;
}

.subject-box__criteria {
  text-align: left;
  padding: 3px 5px !important;
  font-size: 9.5px;
  line-height: 1.25;
  font-weight: 500;
  color: $ink;
  word-break: break-word;
}

.subject-box__key {
  width: 28px;
  text-align: center;
  padding: 2px 1px !important;
}

.key-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 15px;
  padding: 0 3px;
  border-radius: 2px;
  border: 1px solid $line;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;

  &--e {
    background: #c8e6c9;
    color: #1b5e20;
    border-color: #81c784;
  }

  &--m {
    background: #bbdefb;
    color: #0d47a1;
    border-color: #64b5f6;
  }

  &--a {
    background: #ffe0b2;
    color: #e65100;
    border-color: #ffb74d;
  }

  &--n {
    background: #f8d4d4;
    color: #c62828;
    border-color: #e8a0a0;
  }
}

.empty-subjects {
  text-align: center;
  padding: 1rem;
  color: $muted;
  border: 1px dashed $line;
  border-radius: 8px;

  &--sm {
    padding: 0.45rem;
    font-size: 11px;
    border-radius: 0;
    margin-bottom: 0;
  }
}

@media print {
  @page {
    size: A4 landscape;
    margin: 5mm;
  }

  .no-print {
    display: none !important;
  }

  .datasheet-print {
    background: #fff !important;
    padding: 0 !important;
  }

  .sheet-page {
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
  }

  .student-block {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .letterhead__logo,
  .letterhead__logo-fallback {
    width: 70px !important;
    height: 70px !important;
  }

  .letterhead__school {
    font-size: 20px !important;
  }

  .letterhead__tagline,
  .letterhead__note {
    font-size: 11px !important;
  }

  .letterhead__exam {
    font-size: 13px !important;
  }

  .subject-box__title,
  .subject-box__eu-head th,
  .student-info-table__cell,
  .key-pill {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
